import { CreateUserDto, UserRolesEnum } from '@/app/common/general-types/users.dto';
import { FirebaseService } from '@/app/common/firebase/firebase.service';
import { SlickOnboard } from '@/app/pages/onboarding-v2/service/slick-onboard/slick-onboard.service';
import { inject, Injectable, OnInit, signal } from '@angular/core';
import { form, minLength, required } from '@angular/forms/signals';
import { firstValueFrom } from 'rxjs';
import { environment } from '@/environments/environment';
import { HttpClient } from '@angular/common/http';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { AgencyService } from '@/app/services/agency/agency.service';
import { CreateAgencyDto } from '@/app/common/general-types/agency.dto';
import { DOCUMENT } from '@angular/common';

export type AgencyFormModel = {
  agencyName: string;
  agencyDescription: string;
  agencyWebsite: string;
  vatNumber: string;
  currencyCode: string;
};

@Injectable({
  providedIn: 'root',
})
export class SlickOnboardAgency implements OnInit {

  /**
   * Step 1.
   * User Details
   *
   * NB: these details are in the standard Slick Onboard Service,
   * the user details component is re-used.
   */

  /**
   * Step 2.
   * Agency Details
   */
  agencyFormModel = signal<AgencyFormModel>({
    agencyName: '',
    agencyDescription: '',
    agencyWebsite: '',
    vatNumber: '',
    currencyCode: '',
  });

  agencyForm = form(this.agencyFormModel, (schemaPath) => {
    required(schemaPath.agencyName, { message: 'Agency name is required' });
    minLength(schemaPath.agencyName, 2, { message: 'Agency name must be at least 2 characters long' });
    required(schemaPath.agencyWebsite, { message: 'Agency website is required' });
    required(schemaPath.currencyCode, { message: 'Currency is required' });
  });

  /**
   * Step 3.
   * Brand Details
   * Add multiple brands.
   */


  // hubspot tracking code. should be the same cross domain and auto populated by hubspot script
  hubspotUserTokenAgency: string | undefined;

  /** Services */
  private slickOnboardService = inject(SlickOnboard);
  private firebaseService = inject(FirebaseService);
  private http = inject(HttpClient);
  private currentUserService = inject(CurrentUserService);
  private agencyService = inject(AgencyService);
  private document = inject(DOCUMENT);
  /** Services - END  */

  ngOnInit(): void {
    this.getHubspotUserTokenWithDelay();
  }

  async registerAgencyUser() {
    console.log('😳😳😳 Register Agency User.....');

    const personalInfoReq: CreateUserDto = {
      firebaseUid: '',
      email: this.slickOnboardService.userProfileModel().email ?? 'no email...',
      firstName: this.slickOnboardService.userProfileModel().firstName ?? 'First',
      lastName: this.slickOnboardService.userProfileModel().lastName ?? 'Last',
      role: UserRolesEnum["Brand Admin"],
      jobPosition: 'unknown', // To-Do: Add job position
      photoURL: '',
      isActive: true,
      hubspotUserToken: this.hubspotUserTokenAgency ?? 'not-sure-my-bru-agency', // To-Do: Add hubspot user token
      verificationToken: this.slickOnboardService.verificationToken() ?? '',
    };

    console.log('🧩🧩🧩 personalInfoReq: ', personalInfoReq);

    if (this.slickOnboardService.userProfileModel().email && this.slickOnboardService.userProfileModel().password) {
      let deferredResult: unknown = false;

      this.firebaseService.beginDeferPostLoginBootstrap();
      try {
        const newUserResult = await firstValueFrom(
          this.firebaseService.signUp(
            this.slickOnboardService.userProfileModel().email,
            this.slickOnboardService.userProfileModel().password
          )
        );

        if (!newUserResult.user) {
          console.error('Failed to create Agency user 2');
          deferredResult = false;
        } else {
          personalInfoReq.firebaseUid = newUserResult.user.uid;
          const user_register_url = `${environment.shout_api.url}/business-accounts/onboarding-brand`;
          const createUserResult = await firstValueFrom(
            this.http.post(user_register_url, personalInfoReq, {
              headers: {
                'Content-Type': 'application/json',
              },
            })
          );

          if (!createUserResult) {
            console.error('Failed to create Agency user 1');
            deferredResult = null;
          } else {
            console.log('User created successfully: ', createUserResult);
            console.log('🔥🔥🔥 Firebase token: ', this.firebaseService.accessToken);
            this.currentUserService.setShoutUser(createUserResult);

            const agency: CreateAgencyDto = {
              name: this.agencyFormModel().agencyName,
              website: this.agencyFormModel().agencyWebsite,
              about: this.agencyFormModel().agencyDescription,
              currencyCode: this.agencyFormModel().currencyCode,
            };

            console.log('🧩🧩🧩 agency: ', agency);

            try {
              const newAgencyAssociatedRes = await firstValueFrom(
                this.agencyService.associateAgencyToUser(agency, personalInfoReq.firebaseUid)
              );
              console.log('Agency associated successfully: ', newAgencyAssociatedRes);
              deferredResult = newAgencyAssociatedRes;
            } catch (associateAgencyError) {
              console.error('Failed to associate Agency to user: ', associateAgencyError);
              deferredResult = false;
            }
          }
        }
      } catch (signUpError) {
        console.error('Failed to create Agency user 3: ', signUpError);
        deferredResult = false;
      } finally {
        this.firebaseService.endDeferPostLoginBootstrap();
      }

      if (deferredResult && deferredResult !== false) {
        this.firebaseService.runPostLoginBootstrapForCurrentUser();
      }

      return deferredResult;
    }

    return false;
  }

  getHubspotUserTokenWithDelay() {
    setTimeout(() => {
      this.hubspotUserTokenAgency = this.getCookie('hubspotutk');
      console.log('HubSpot User Token:', this.hubspotUserTokenAgency);
      if (!this.hubspotUserTokenAgency) {
        console.log('HubSpot User Token is not available yet. Retrying...');
      }
    }, 500); // 500ms delay
  }

  // A helper function to parse cookies from document.cookie
  private getCookie(name: string): string | undefined {
    const cookies = this.document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i].trim();
      // Does this cookie string begin with the name we want?
      if (cookie.startsWith(name + '=')) {
        return cookie.substring(name.length + 1, cookie.length);
      }
    }
    return undefined;
  }
}
